import { request } from "./http";
import { DEFAULT_REACTION_KIND } from "../ui/reactions/reactionKinds";
import { buildReactionTogglePayload, normalizeReactionKindForApi, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

function encode(value) {
  return encodeURIComponent(String(value ?? "").trim());
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function unwrapItem(payload) {
  const obj = asObject(payload);
  if (obj.item && typeof obj.item === "object") return obj.item;
  return obj;
}

function withPaging(path, { page = 0, size = 20 } = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(Number(page) || 0));
  qs.set("size", String(Number(size) || 20));
  const suffix = qs.toString();
  return suffix ? `${path}${path.includes("?") ? "&" : "?"}${suffix}` : path;
}

export const literatureApi = {
  // ----- Public -----
  getCatalog({ q = "", topic = "", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(q).trim()) qs.set("q", String(q).trim());
    if (String(topic).trim()) qs.set("topic", String(topic).trim());
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));
    return request(`/api/public/literature/catalog?${qs.toString()}`);
  },

  getBook(bookIdOrSlug) {
    return request(`/api/public/literature/books/${encode(bookIdOrSlug)}`);
  },

  getBookRecommendations(bookIdOrSlug, { page = 0, size = 20 } = {}) {
    return request(withPaging(`/api/public/literature/books/${encode(bookIdOrSlug)}/recommendations`, { page, size }));
  },

  getBookComments(bookIdOrSlug) {
    return request(`/api/public/literature/books/${encode(bookIdOrSlug)}/comments`);
  },

  getBookSimilar(bookIdOrSlug, { page = 0, size = 20 } = {}) {
    return request(withPaging(`/api/public/literature/books/${encode(bookIdOrSlug)}/similar`, { page, size }));
  },

  getTopicBooks(topic, { page = 0, size = 20 } = {}) {
    return request(withPaging(`/api/public/literature/topics/${encode(topic)}/books`, { page, size }));
  },

  getForMe({ page = 0, size = 20 } = {}) {
    return request(withPaging("/api/public/literature/for-me", { page, size }));
  },

  getFeatured({ topic = "", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(topic).trim()) qs.set("topic", String(topic).trim());
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));
    return request(`/api/public/literature/featured?${qs.toString()}`);
  },

  getLatest({ page = 0, size = 20 } = {}) {
    return request(withPaging("/api/public/literature/latest", { page, size }));
  },

  getPsychologistShelf(psychologistUserId, { tab = "ALL", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    qs.set("tab", String(tab || "ALL"));
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));
    return request(`/api/public/psychologists/${encode(psychologistUserId)}/literature-shelf?${qs.toString()}`);
  },

  startBookCheckout(
    bookId,
    { returnUrl = "", cancelUrl = "", paymentMethod = "card" } = {}
  ) {
    const cleanReturnUrl = String(returnUrl || "").trim();
    const cleanCancelUrl = String(cancelUrl || "").trim();
    const method = String(paymentMethod || "card").trim().toLowerCase() || "card";
    return request(`/api/literature/books/${encode(bookId)}/checkout`, {
      method: "POST",
      json: {
        paymentMethod: method,
        returnUrl: cleanReturnUrl,
        cancelUrl: cleanCancelUrl,
        yookassa: {
          paymentMethod: method,
          returnUrl: cleanReturnUrl,
        },
      },
    });
  },

  // ----- Literature interactions -----
  createBookComment(bookId, { parentCommentId = null, content = "", rating = null } = {}) {
    const numericRating = Number(rating);
    const normalizedRating =
      Number.isFinite(numericRating) && numericRating >= 1 && numericRating <= 5
        ? Math.round(numericRating)
        : null;
    return request(`/api/literature/books/${encode(bookId)}/comments`, {
      method: "POST",
      json: {
        parentCommentId: parentCommentId == null ? null : Number(parentCommentId),
        content: String(content || "").trim(),
        ...(normalizedRating != null
          ? {
              rating: normalizedRating,
              stars: normalizedRating,
              reviewRating: normalizedRating,
            }
          : {}),
      },
    });
  },

  updateComment(commentId, { parentCommentId = null, content = "", rating = null } = {}) {
    const numericRating = Number(rating);
    const normalizedRating =
      Number.isFinite(numericRating) && numericRating >= 1 && numericRating <= 5
        ? Math.round(numericRating)
        : null;
    const payload = {
      parentCommentId: parentCommentId == null ? null : Number(parentCommentId),
      content: String(content || "").trim(),
      ...(normalizedRating != null
        ? {
            rating: normalizedRating,
            stars: normalizedRating,
            reviewRating: normalizedRating,
          }
        : {}),
    };
    // В PATCH не отправляем пустые поля (кроме content) чтобы не затирать серверные значения.
    if (!payload.content) delete payload.content;
    if (payload.parentCommentId == null) delete payload.parentCommentId;
    return request(`/api/literature/comments/${encode(commentId)}`, {
      method: "PATCH",
      json: payload,
    });
  },

  toggleBookReaction(bookId, reactionType = DEFAULT_REACTION_KIND) {
    const type = normalizeReactionKindForApi(reactionType, DEFAULT_REACTION_KIND);
    const json = buildReactionTogglePayload({
      reaction: type,
      targetType: REACTION_TARGET_TYPES.LITERATURE_BOOK,
      targetId: bookId,
    });
    return request(`/api/literature/books/${encode(bookId)}/reactions/toggle`, {
      method: "POST",
      json,
    });
  },

  searchUsers({ q = "", role = "", limit = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(q).trim()) qs.set("q", String(q).trim());
    if (String(role).trim()) qs.set("role", String(role).trim());
    qs.set("limit", String(Number(limit) || 20));
    return request(`/api/users/search?${qs.toString()}`);
  },

  recommendBook(
    bookId,
    {
      recipientUserId,
      comment = "",
      sendNotification = true,
      duplicateToChat = true,
      openOrCreateDialog = true,
    } = {}
  ) {
    return request(`/api/literature/books/${encode(bookId)}/recommend`, {
      method: "POST",
      json: {
        recipientUserId: Number(recipientUserId),
        comment: String(comment || "").trim(),
        sendNotification: Boolean(sendNotification),
        duplicateToChat: Boolean(duplicateToChat),
        openOrCreateDialog: Boolean(openOrCreateDialog),
      },
    });
  },

  toggleCommentReaction(commentId, reactionType = DEFAULT_REACTION_KIND) {
    const type = normalizeReactionKindForApi(reactionType, DEFAULT_REACTION_KIND);
    const json = buildReactionTogglePayload({
      reaction: type,
      targetType: REACTION_TARGET_TYPES.LITERATURE_COMMENT,
      targetId: commentId,
    });
    return request(`/api/literature/comments/${encode(commentId)}/reactions/toggle`, {
      method: "POST",
      json,
    });
  },

  reportBookComment(commentId, { reason = "", details = "" } = {}) {
    return request(`/api/literature/comments/${encode(commentId)}/report`, {
      method: "POST",
      json: {
        reason: String(reason || "").trim(),
        details: String(details || "").trim(),
      },
    });
  },

  // ----- My client cabinet -----
  getMySaved({ page = 0, size = 20 } = {}) {
    return request(withPaging("/api/me/literature/saved", { page, size }));
  },

  saveBook(bookId) {
    return request(`/api/me/literature/books/${encode(bookId)}/save`, {
      method: "POST",
      json: {},
    });
  },

  unsaveBook(bookId) {
    return request(`/api/me/literature/books/${encode(bookId)}/save`, {
      method: "DELETE",
    });
  },

  getMyContentComments({ page = 0, size = 20 } = {}) {
    return request(withPaging("/api/me/content/literature/comments", { page, size }));
  },

  getMyBookProgress(bookId) {
    return request(`/api/me/literature/books/${encode(bookId)}/progress`);
  },

  updateMyBookProgress(bookId, { progressPercent = 0 } = {}) {
    const raw = Number(progressPercent);
    const normalized = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
    return request(`/api/me/literature/books/${encode(bookId)}/progress`, {
      method: "PUT",
      json: { progressPercent: normalized },
    });
  },

  // ----- Psychologist cabinet -----
  getMyBooks({ page = 0, size = 20 } = {}) {
    return request(withPaging("/api/psychologist/literature/books", { page, size }));
  },

  createBook(payload = {}) {
    return request("/api/psychologist/literature/books", {
      method: "POST",
      json: payload,
    }).then(unwrapItem);
  },

  updateBook(bookId, payload = {}) {
    return request(`/api/psychologist/literature/books/${encode(bookId)}`, {
      method: "PUT",
      json: payload,
    }).then(unwrapItem);
  },

  getLitresStatus() {
    return request("/api/psychologist/literature/integrations/litres/status");
  },

  getLitresCatalogList({ ids = [], uuids = [] } = {}) {
    const qs = new URLSearchParams();
    (Array.isArray(ids) ? ids : []).forEach((id) => qs.append("ids", String(id)));
    (Array.isArray(uuids) ? uuids : []).forEach((uuid) => qs.append("uuids", String(uuid)));
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return request(`/api/psychologist/literature/integrations/litres/catalog/list${tail}`);
  },

  importLitres(payload = {}) {
    return request("/api/psychologist/literature/import/litres", {
      method: "POST",
      json: payload,
    });
  },

  getBookExternalStore(bookId) {
    return request(`/api/psychologist/literature/books/${encode(bookId)}/external-store`);
  },

  updateBookExternalStore(bookId, payload = {}) {
    return request(`/api/psychologist/literature/books/${encode(bookId)}/external-store`, {
      method: "PUT",
      json: payload,
    });
  },

  deleteBookExternalStore(bookId) {
    return request(`/api/psychologist/literature/books/${encode(bookId)}/external-store`, {
      method: "DELETE",
    });
  },

  syncBookExternalStore(bookId, { overwriteLocalFields = false } = {}) {
    return request(
      `/api/psychologist/literature/books/${encode(bookId)}/external-store/sync?overwriteLocalFields=${overwriteLocalFields ? "true" : "false"}`,
      {
        method: "POST",
        json: {},
      }
    );
  },

  getMyShelf({ tab = "ALL", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    qs.set("tab", String(tab || "ALL"));
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));
    return request(`/api/psychologist/literature/shelf?${qs.toString()}`);
  },

  addShelfBook(bookId, payload = {}) {
    return request(`/api/psychologist/literature/shelf/books/${encode(bookId)}`, {
      method: "POST",
      json: payload,
    });
  },

  updateShelfBook(bookId, payload = {}) {
    return request(`/api/psychologist/literature/shelf/books/${encode(bookId)}`, {
      method: "PUT",
      json: payload,
    });
  },

  removeShelfBook(bookId) {
    return request(`/api/psychologist/literature/shelf/books/${encode(bookId)}`, {
      method: "DELETE",
    });
  },

  // ----- Admin -----
  getAdminBooks({ q = "", ownerUserId = "", published = "", showInCatalog = "", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(q).trim()) qs.set("q", String(q).trim());
    if (String(ownerUserId).trim()) qs.set("ownerUserId", String(ownerUserId).trim());
    if (String(published).trim()) qs.set("published", String(published).trim());
    if (String(showInCatalog).trim()) qs.set("showInCatalog", String(showInCatalog).trim());
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));
    return request(`/api/admin/literature/books?${qs.toString()}`);
  },
};
